import { useEffect, useState } from "react";
import { apiPatch } from "lib/apiClient";

function errorMessage(err) {
  if (err?.code === "FORBIDDEN") return "Only a tenant administrator can change homepage placement.";
  if (err?.code === "MARKET_NOT_FOUND") return "This market could not be found.";
  return err?.message || "Could not change homepage placement. Try again.";
}

/**
 * "Show on homepage" for one market (tenant seat). Turning it off pulls the market from the
 * storefront homepage and the /partners shelves; it stays tradable and listed elsewhere.
 * `hidden` may be undefined when the caller's data does not carry it yet: then both
 * choices are offered until the first change tells us the state.
 */
export default function HomepageToggle({ marketId, hidden, canEdit, onChanged, compact = false }) {
  const [value, setValue] = useState(hidden);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => setValue(hidden), [hidden]);

  async function apply(nextHidden) {
    setBusy(true);
    setError(null);
    try {
      const result = await apiPatch(`/portal/markets/${marketId}/homepage`, { hidden: nextHidden });
      setValue(typeof result?.homepageHidden === "boolean" ? result.homepageHidden : nextHidden);
      onChanged?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const known = typeof value === "boolean";
  const shown = known ? !value : null;
  const description = "Off pulls the market from the homepage and the partner shelves. It stays live and tradable.";

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        {canEdit && known ? (
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              role="switch"
              aria-checked={shown}
              checked={shown}
              disabled={busy}
              onChange={(e) => apply(!e.target.checked)}
              className={`relative h-5 w-10 shrink-0 appearance-none rounded-xl bg-gray-200 outline-none transition duration-300 before:absolute before:top-1/2 before:h-4 before:w-4 before:translate-x-[2px] before:-translate-y-1/2 before:rounded-full before:bg-white before:shadow before:transition before:content-[""] checked:bg-primary checked:before:translate-x-[22px] hover:cursor-pointer disabled:opacity-50 dark:bg-white/10`}
            />
            <span className="text-sm font-medium text-navy-700 dark:text-white">Show on homepage</span>
          </label>
        ) : canEdit ? (
          <>
            <span className="text-sm font-medium text-navy-700 dark:text-white">Homepage</span>
            <button type="button" disabled={busy} onClick={() => apply(false)} className="rounded-lg bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-600 disabled:opacity-50 dark:bg-green-400/10 dark:text-green-300">
              Show on homepage
            </button>
            <button type="button" disabled={busy} onClick={() => apply(true)} className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-500 disabled:opacity-50 dark:bg-red-400/10 dark:text-red-300">
              Hide from homepage
            </button>
          </>
        ) : (
          <span className="text-sm text-navy-700 dark:text-white">{known ? (shown ? "Shown on the homepage" : "Hidden from the homepage") : "Homepage placement unknown"}</span>
        )}
        {busy && <span className="text-xs text-gray-500">Saving…</span>}
      </div>
      {!compact && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{description}</p>}
      {error && <p className="mt-1 text-xs font-medium text-red-500">{error}</p>}
    </div>
  );
}
